import { createEntityStore } from '@redpanda/utils';
import { toast } from 'react-hot-toast';
import { getInvoices, deleteInvoice as deleteInvoiceApi } from '../services/api';
import { storeMessages } from './storeMessages';
import { ymd } from '../utils/dates';

export const INVOICE_STATUSES = ['DRAFT', 'SENT', 'PAID', 'OVERDUE', 'CANCELLED'] as const;
export type InvoiceStatus = typeof INVOICE_STATUSES[number];

export interface InvoiceItem {
  id?: string;
  description: string;
  quantity: number | string;
  unitPrice: number | string;
  amount?: number | string;
}

export interface Invoice {
  id: string;
  userId?: string;
  number: string;
  date: string;
  dueDate?: string | null;
  customerName: string;
  customerAddress?: string | null;
  customerTaxId?: string | null;
  subtotal: number | string;
  taxAmount: number | string;
  totalAmount: number | string;
  taxRate: number | string;
  status: InvoiceStatus;
  notes?: string | null;
  items: InvoiceItem[];
  archived?: boolean;
  createdAt?: string;
  /** 收入科目（空＝4101 營業收入） */
  revenueAccountId?: string | null;
  /** 開立時自動產生的分錄；空＝草稿或上線前開立的舊發票 */
  journalEntryId?: string | null;
  paidAmount?: number | string;
  /** 折讓沖抵的應收金額 */
  creditedAmount?: number | string;
  payments?: InvoicePayment[];
  creditNotes?: CreditNote[];
}

export interface CreditNote {
  id: string;
  number: string;
  date: string;
  reason?: string | null;
  subtotal: number | string;
  taxAmount: number | string;
  totalAmount: number | string;
  refundAmount: number | string;
}

export interface InvoicePayment {
  id: string;
  date: string;
  amount: number | string;
  bankAccount?: { bankName: string; accountName: string } | null;
}

/** 未收金額（總額－已收－折讓沖抵） */
export const outstandingOf = (invoice: Invoice) =>
  Math.max(0, Number(invoice.totalAmount) - Number(invoice.paidAmount ?? 0) - Number(invoice.creditedAmount ?? 0));

/** 有收款或折讓記錄 */
export const hasSettlements = (invoice: Invoice) => (invoice.payments?.length ?? 0) > 0 || (invoice.creditNotes?.length ?? 0) > 0;

/** 只能改到期日、地址、備註：已收款、有收款或折讓（後端 INVOICE_LOCKED、INVOICE_HAS_PAYMENTS 同規則；同 QuickBooks） */
export const isInvoiceLimited = (invoice: Invoice) => invoice.status === 'PAID' || hasSettlements(invoice);

/** 可以刪除：還沒記帳（沒有分錄、收款、折讓）且不是已收款；已記帳的要作廢（後端 INVOICE_POSTED 同規則） */
export const isInvoiceDeletable = (invoice: Invoice) =>
  invoice.status !== 'PAID' && !invoice.journalEntryId && !hasSettlements(invoice);

/** 畫面顯示的狀態：已開立且過了到期日＝逾期（同儀表板、後端統計） */
export const displayStatus = (invoice: Invoice): InvoiceStatus =>
  invoice.status === 'SENT' && invoice.dueDate && ymd(new Date(invoice.dueDate)) < ymd(new Date()) ? 'OVERDUE' : invoice.status;

export const useInvoiceStoreBase = createEntityStore<Invoice>({
  entityName: 'invoice',
  fetchFn: (page: number) => getInvoices(page),
  deleteFn: deleteInvoiceApi,
  toast,
  messages: storeMessages,
});

export const useArchivedInvoiceStoreBase = createEntityStore<Invoice>({
  entityName: 'archivedInvoice',
  fetchFn: (page: number) => getInvoices(page, 20, { archived: true }),
  deleteFn: deleteInvoiceApi,
  toast,
  messages: storeMessages,
});

/** 編輯、改狀態後寫回兩個列表（id 不在該列表時 updateItem 不動作） */
export const updateInvoiceInLists = (id: string, data: Partial<Invoice>) => {
  useInvoiceStoreBase.getState().updateItem(id, data);
  useArchivedInvoiceStoreBase.getState().updateItem(id, data);
};
